import {
  Box,
  FormControl,
  InputLabel,
  OutlinedInput,
  Button,
  Stack,
  Tooltip,
} from '@mui/material';
import cx from 'classnames';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router';

import { DashboardDrawer } from './components/DashboardDrawer';
import { DashboardsTable } from './components/DashboardsTable';
import { useColumns } from './components/DashboardsTable/hooks/useColumns';
import { DashboardUsageLimitChip } from './components/DashboardUsageLimitChip';
import { DASHBOARD_ID_PARAMETER_NAME } from './constants';
import {
  DASHBOARDS_OVERVIEW_STORAGE_KEYS,
  dashboardsOverviewConfig,
  defaultTableColumnVisibility,
} from './dashboardsOverview.config';
import styles from './DashboardsOverview.module.scss';
import { useDashboardsOverviewFilterViewStore } from './useDashboardsOverviewFilterViewStore';
import { useGetDashboardsUsageLimit } from '@/api/REST/dashboards';
import { Dashboard } from '@/api/typesense/dashboards';
import { FilterBar, Views } from '@/components/filters';
import { PageTitle } from '@/components/global/PageTitle';
import { Panels } from '@/components/layouts/Panels';
import { Icon } from '@/components/ui/Icon/Icon';
import { IconButton } from '@/components/ui/IconButton';
import { PageHeader } from '@/components/ui/PageHeader';
import { ViewOptionsPopover } from '@/components/ui/ViewOptionsPopover';
import { areaIcons } from '@/constants/areaIcons';
import { routes } from '@/constants/routes';
import { uiIcons } from '@/constants/uiIcons';
import { useSearchResults, useFilterManager } from '@/hooks/filters';
import { useOrderedColumns, useTablePreferences } from '@/hooks/grid';
import { usePanelDrawer, usePopover, useSearchInput } from '@/hooks/ui';
import type { FilterClause } from '@/lib/filter-dsl';

export const DashboardsOverview = () => {
  const { t } = useTranslation(['dashboardsOverview', 'dashboardCreation']);

  const [searchParams] = useSearchParams();
  const selectedDashboardId = searchParams.get(DASHBOARD_ID_PARAMETER_NAME);

  const {
    data: { user: userUsageLimit } = {},
    isLoading: isLoadingUsageLimit,
  } = useGetDashboardsUsageLimit();

  const { searchInput, setSearchInput, debouncedSearchInput } =
    useSearchInput();

  const {
    isPanelOpen,
    expandDrawer,
    drawerRef,
    openPanel,
    openPanelAndFocus,
    closePanel,
    handleEscapeFromDrawer,
  } = usePanelDrawer({ selectedId: selectedDashboardId });

  // Get filter manager (manages both filters and views)
  const {
    filterSet,
    removeFilter,
    updateFilter,
    filterViews,
    currentFilters: _currentFilters,
    currentViewId,
    currentView,
    filterPanelOpen,
    selectView,
    saveCurrentView,
    saveAsNewView,
    createEmptyView,
    resetFilters,
    clearViewAndFilterSelection,
    toggleFilterPanel,
    hasUnsavedChanges,
    hasFilters,
    isSaving,
    isTransitioning,
    isCreatingView,
    isSavingAsNewView,
    startCreatingView,
    endCreatingView,
    startSavingAsNewView,
    endSavingAsNewView,
    duplicateView,
    createViewMutation,
    updateViewMutation,
    deleteViewMutation,
  } = useFilterManager({
    useStore: useDashboardsOverviewFilterViewStore,
  });

  // Table view preferences (no cards view for dashboards)
  const {
    tableSortModel: activeSortModel,
    tableColumnVisibility: activeColumnVisibility,
    handleTableColumnToggle: activeHandleColumnToggle,
    columnWidths,
    columnOrder,
    density,
    setDensity,
    setTableColumnVisibility,
    handleColumnWidthChange,
    handleColumnOrderChange,
    setTableSortModel,
  } = useTablePreferences(DASHBOARDS_OVERVIEW_STORAGE_KEYS, {
    tableColumnVisibility: defaultTableColumnVisibility,
    tableSort: [{ field: 'Name', sort: 'asc' }],
  });

  // Fetch search results with infinite scroll
  const { data, isLoading, hasNextPage, fetchNextPage } =
    useSearchResults<Dashboard>({
      config: dashboardsOverviewConfig,
      filterSet: [
        ...filterSet,
        // Always exclude templates from results since they are not relevant for the overview page
        {
          facet: 'dashboardType',
          operator: 'not',
          values: ['Template'],
        },
      ],
      sortModel: activeSortModel,
      query: debouncedSearchInput, // Debounced search query (200ms delay) s
    });

  const allDashboards = data?.pages?.flatMap((page) => page.documents) || [];
  const totalResults = data?.pages?.[0]?.totalResults || 0;

  const handleAddFilter = (clause: FilterClause) => {
    updateFilter(clause);
  };

  // View options popover state
  const viewOptions = usePopover({ id: 'dashboards-view-options' });

  // Get columns with width preferences
  const allColumns = useColumns(columnWidths);

  // Reorder columns based on saved order
  const columns = useOrderedColumns(allColumns, columnOrder);

  // keep for future iteration
  // const [isCreationModalOpen, setIsCreationModalOpen] = useState(false);
  // const openNewDashboardModal = () => setIsCreationModalOpen(true);

  return (
    <>
      <PageTitle title={t('PageTitle')} />
      <Panels
        autoSaveId="dashboards-overview-panels"
        expandDrawer={expandDrawer}
        collapseDrawerOnEscape
        onDrawerCollapse={closePanel}
        drawerRef={drawerRef}
        onEscapeFromDrawer={handleEscapeFromDrawer}
      >
        <Panels.Main
          panelContentProps={{
            hideOverflow: true,
          }}
        >
          <PageHeader title={t('PageTitle')} iconName={areaIcons.dashboards}>
            {
              <PageHeader.Info>
                <Stack direction="row" alignItems="center" gap={2}>
                  <DashboardUsageLimitChip type="user" />
                  <Tooltip
                    title={
                      !userUsageLimit?.canCreate
                        ? t('dashboardCreation:CreationDisabledTooltip')
                        : ''
                    }
                  >
                    {/* workaround needed for disabled styled a tag buttons */}
                    <span
                      className={cx({
                        [styles.NewDashboardButtonDisabledContainer]:
                          isLoadingUsageLimit || !userUsageLimit?.canCreate,
                      })}
                    >
                      <Button
                        variant="contained"
                        component={Link}
                        to={routes.reporting.new()}
                        color="primary"
                        size="small"
                        startIcon={<Icon name={uiIcons.actions.add} />}
                        // keep for future iteration
                        // onClick={openNewDashboardModal}
                        disabled={
                          isLoadingUsageLimit || !userUsageLimit?.canCreate
                        }
                        data-automation-id="dashboards-overview-new-dashboard-button"
                      >
                        {t('NewDashboardButton')}
                      </Button>
                    </span>
                  </Tooltip>
                </Stack>
              </PageHeader.Info>
            }
          </PageHeader>

          {/* Search and Controls */}
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="start"
            gap={2}
          >
            {/* View tabs - shows saved views */}
            <Views
              filterViews={filterViews}
              createView={createViewMutation}
              createEmptyView={createEmptyView}
              saveAsNewView={saveAsNewView}
              duplicateView={duplicateView}
              updateView={updateViewMutation}
              deleteViewMutation={deleteViewMutation}
              currentViewId={currentViewId}
              hasUnsavedChanges={hasUnsavedChanges}
              onViewSelect={(view) => {
                if (view?.filterViewId) {
                  // Pass filterParameters to handle newly created views
                  selectView(view.filterViewId, view.filterParameters);
                } else {
                  clearViewAndFilterSelection();
                }
              }}
              isCreatingView={isCreatingView}
              isSavingAsNewView={isSavingAsNewView}
              onCreateStart={startCreatingView}
              onCreateEnd={endCreatingView}
              onSaveAsNewEnd={endSavingAsNewView}
              filterPanelOpen={filterPanelOpen}
              onToggleFilterPanel={toggleFilterPanel}
            />

            <Stack direction="row" alignItems="center" gap={1}>
              <FormControl>
                {/* This div is needed to show the placeholder */}
                <div>
                  <InputLabel visuallyHidden htmlFor="search-input">
                    {t('SearchLabel', { ns: 'ui' })}
                  </InputLabel>
                </div>
                <OutlinedInput
                  id="search-input"
                  type="search"
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  placeholder={t('SearchLabel', { ns: 'ui' })}
                  pilled
                  size="small"
                  data-automation-id="dashboards-overview-search-input"
                  className={styles.SearchInput}
                />
              </FormControl>

              <IconButton
                size="small"
                color="secondary"
                tooltip={t('tooltip.ViewOptions')}
                iconName={uiIcons.controls.columnPicker}
                variant="outlined"
                onClick={viewOptions.handleOpen}
                aria-haspopup="dialog"
                aria-expanded={viewOptions.open ? 'true' : undefined}
                aria-controls={viewOptions.open ? viewOptions.id : undefined}
                data-automation-id="dashboards-overview-view-options-button"
              />

              <ViewOptionsPopover
                id={viewOptions.id}
                open={viewOptions.open}
                anchorEl={viewOptions.anchorEl}
                onClose={viewOptions.handleClose}
                columns={columns}
                columnVisibilityModel={activeColumnVisibility}
                onColumnToggle={activeHandleColumnToggle}
                density={density}
                onDensityChange={setDensity}
              />
            </Stack>
          </Stack>

          <FilterBar
            id="filter-panel"
            aria-expanded={filterPanelOpen}
            hidden={!filterPanelOpen}
            config={dashboardsOverviewConfig}
            filterSet={filterSet}
            onAddFilter={handleAddFilter}
            onRemoveFilter={removeFilter}
            onUpdateFilter={updateFilter}
            currentView={currentView}
            hasUnsavedChanges={hasUnsavedChanges}
            hasFilters={hasFilters}
            isSaving={isSaving}
            isTransitioning={isTransitioning}
            onSaveCurrentView={saveCurrentView}
            onSaveAsNewView={startSavingAsNewView}
            onResetFilters={resetFilters}
          />

          <Box sx={{ flex: 1, minHeight: 0 }}>
            <DashboardsTable
              items={allDashboards}
              isLastPage={!hasNextPage}
              showMore={fetchNextPage}
              columns={columns}
              columnVisibilityModel={activeColumnVisibility}
              onColumnVisibilityModelChange={setTableColumnVisibility}
              columnWidths={columnWidths}
              onColumnWidthChange={handleColumnWidthChange}
              onColumnOrderChange={handleColumnOrderChange}
              sortModel={activeSortModel}
              onSortModelChange={setTableSortModel}
              density={density}
              loading={isLoading}
              isPanelOpen={isPanelOpen}
              onPanelOpen={openPanel}
              onPanelOpenAndFocus={openPanelAndFocus}
              totalResults={totalResults}
            />
          </Box>
        </Panels.Main>
        <Panels.Drawer>
          <DashboardDrawer />
        </Panels.Drawer>
      </Panels>
      {/* keep for future iteration */}
      {/* <DashboardCreationModal
        open={isCreationModalOpen}
        onClose={() => setIsCreationModalOpen(false)}
      /> */}
    </>
  );
};
